import { useEffect, useState } from 'react';
import { isAppInstalled } from '@/pwa/installPrompt';

/**
 * Tracks whether the app is running as an installed PWA (standalone display
 * mode, or iOS "Add to Home Screen"). Installed windows have no browser
 * chrome — and therefore no address bar or reload button — so callers use
 * this to surface an in-app equivalent (e.g. a Refresh control).
 *
 * The value can change at runtime (a tab can enter/leave standalone), so we
 * subscribe to the `display-mode` media query and keep it in sync.
 */
export function useIsAppInstalled(): boolean {
  const [installed, setInstalled] = useState<boolean>(() => isAppInstalled());

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }

    const query = window.matchMedia('(display-mode: standalone)');
    const update = () => setInstalled(isAppInstalled());

    // Re-check on entering/leaving standalone and when the tab regains focus
    // (iOS reports standalone via navigator.standalone, not the media query).
    query.addEventListener('change', update);
    window.addEventListener('focus', update);

    return () => {
      query.removeEventListener('change', update);
      window.removeEventListener('focus', update);
    };
  }, []);

  return installed;
}
